import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, useCurrency, API } from "../App";
import { ClipboardList, LogOut, User, BarChart3, MessageSquare, UserCircle, Undo2, Store, Menu, X, ChevronLeft, ChevronRight, Monitor, LayoutDashboard, UtensilsCrossed, CreditCard, Percent, FolderOpen, Hash, Users, Wallet, Shield, Leaf, Settings, Database, PhoneCall, ChefHat, Truck, Tag, Key, Bell, Check, Users2, Building2, Gift, Ruler, Beaker, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { setupStaffPushNotifications, playChime } from "../lib/push";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const logoSrc = (url) => url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "/logo-white.png";

// Mobile-allowed nav items (limited set for mobile operations)
const MOBILE_NAV_IDS = new Set(["dashboard", "menu", "orders", "kitchen-menus", "refunds", "reviews", "reports", "profile", "deliveries", "recipes", "recipe-categories", "recipe-tags", "recipe-operational-units", "recipe-metric-units", "home-banners"]);

// Stable, light-on-dark Tailwind chip class for a kitchen code. Uses a small palette
// so multi-kitchen admins can tell pings apart at a glance.
const KITCHEN_CHIP_PALETTE = [
  "bg-orange-100 text-orange-700",
  "bg-blue-100 text-blue-700",
  "bg-purple-100 text-purple-700",
  "bg-amber-100 text-amber-700",
  "bg-pink-100 text-pink-700",
  "bg-teal-100 text-teal-700",
  "bg-indigo-100 text-indigo-700",
  "bg-rose-100 text-rose-700",
];
function kitchenChipClass(code) {
  if (!code) return "bg-gray-100 text-gray-500";
  let h = 0;
  for (let i = 0; i < code.length; i++) h = (h * 31 + code.charCodeAt(i)) >>> 0;
  return KITCHEN_CHIP_PALETTE[h % KITCHEN_CHIP_PALETTE.length];
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const { logoUrl } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sm_sidebar_collapsed") === "true");

  const isManager = user?.role === "manager";
  const isAdmin = user?.role === "admin";
  const isRider = user?.role === "rider";
  const isChef = user?.role === "chef";
  const isNutritionist = user?.role === "nutritionist";

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // --- Background new-order poller + notifications center ---
  // Runs for admin, manager and chef roles. Every 30s fetches /api/orders/admin,
  // compares against the last known set of order IDs, and for any new order
  // plays an alert sound + adds it to the notifications list shown in the bell icon.
  const seenOrderIdsRef = useRef(null); // Set<string>; null on very first fetch
  const NOTIFS_KEY = `sm_admin_notifs_${user?.user_id || "anon"}`;
  const [adminNotifs, setAdminNotifs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NOTIFS_KEY) || "[]"); }
    catch { return []; }
  });
  const [bellOpen, setBellOpen] = useState(false);
  const unreadAdminCount = adminNotifs.filter((n) => !n.read).length;

  const persistNotifs = (arr) => {
    // Cap at last 50
    const capped = arr.slice(0, 50);
    setAdminNotifs(capped);
    try { localStorage.setItem(NOTIFS_KEY, JSON.stringify(capped)); }
    catch { /* quota exceeded */ }
  };

  useEffect(() => {
    if (!user) return;
    const allowedRoles = new Set(["admin", "manager", "chef"]);
    if (!allowedRoles.has(user.role)) return;

    // Request browser notification permission (idempotent)
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch { /* no-op */ }
    }

    // Subscribe this device to FCM push so background notifications fire
    // even when the PWA / Capacitor app is closed. Idempotent + silent on
    // failure — does not block in-app polling fallback below.
    setupStaffPushNotifications();

    // playChime is imported from ../lib/push — uses the shared <audio> that
    // is iOS-autoplay-unlocked on the first user gesture so the in-app
    // chime works on Safari PWA too.

    const tick = async () => {
      try {
        const res = await fetch(`${API}/orders/admin`, { credentials: "include" });
        if (!res.ok) return;
        const orders = await res.json();
        const currentIds = new Set(orders.map((o) => o.order_id));

        if (seenOrderIdsRef.current === null) {
          seenOrderIdsRef.current = currentIds;
          return;
        }

        const newOrders = orders.filter((o) => !seenOrderIdsRef.current.has(o.order_id));
        if (newOrders.length > 0) {
          playChime();
          setAdminNotifs((prev) => {
            const existingIds = new Set(prev.map((n) => n.order_id));
            const toAdd = newOrders
              .filter((o) => !existingIds.has(o.order_id))
              .map((o) => ({
                order_id: o.order_id,
                order_number: o.order_number,
                user_name: o.user_name || "Customer",
                items_count: (o.items || []).length,
                total_amount: o.total_amount,
                kitchen_code: o.kitchen_code || "",
                created_at: o.created_at,
                read: false,
                ts: Date.now(),
              }));
            const merged = [...toAdd, ...prev].slice(0, 50);
            try { localStorage.setItem(NOTIFS_KEY, JSON.stringify(merged)); }
            catch { /* ignore */ }
            return merged;
          });
          newOrders.slice(0, 3).forEach((o) => {
            toast.info(`New order ${o.order_number || o.order_id.slice(0, 8)} — ${o.user_name || "customer"}`);
            if ("Notification" in window && Notification.permission === "granted") {
              try {
                new Notification("New order received", {
                  body: `${o.order_number || o.order_id.slice(0, 8)} · ${o.items?.length || 0} items`,
                  icon: "/favicon.ico",
                });
              } catch { /* ignore */ }
            }
          });
        }
        seenOrderIdsRef.current = currentIds;
      } catch { /* silent network errors */ }
    };

    tick();
    const intervalId = setInterval(tick, 30000);

    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleNotifClick = (notif) => {
    // Mark this notification read and navigate
    const updated = adminNotifs.map((n) => n.order_id === notif.order_id ? { ...n, read: true } : n);
    persistNotifs(updated);
    setBellOpen(false);
    navigate(`/admin/orders?highlight=${notif.order_id}`);
  };

  const markAllRead = () => {
    persistNotifs(adminNotifs.map((n) => ({ ...n, read: true })));
  };

  const clearAllNotifs = () => {
    persistNotifs([]);
  };

  const formatNotifTime = (ts) => {
    if (!ts) return "";
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return new Date(ts).toLocaleDateString();
  };

  const isAdminRole = new Set(["admin", "manager", "chef", "nutritionist"]).has(user?.role);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const toggleDesktopSidebar = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sm_sidebar_collapsed", String(next));
  };

  // Full nav items for desktop
  // Nutritionist sees a tightly scoped nav (recipes + profile only).
  const allNavItems = isNutritionist
    ? [
        { to: "/admin/recipes", icon: ChefHat, label: "Recipes", end: true, id: "recipes" },
        { to: "/admin/recipe-categories", icon: FolderOpen, label: "Recipe Categories", id: "recipe-categories" },
        { to: "/admin/recipe-tags", icon: Tag, label: "Recipe Tags", id: "recipe-tags" },
        { to: "/admin/recipe-operational-units", icon: Ruler, label: "Recipe Operational Units", id: "recipe-operational-units" },
        { to: "/admin/recipe-metric-units", icon: Beaker, label: "Recipe Metric Units", id: "recipe-metric-units" },
        { divider: true, id: "d4" },
        { to: "/admin/profile", icon: UserCircle, label: "My Profile", id: "profile" },
      ]
    : [
    ...(!isRider && !isChef ? [{ to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, id: "dashboard" }] : []),
    ...(!isRider ? [{ to: "/admin/menu", icon: UtensilsCrossed, label: "Menu Items", id: "menu" }] : []),
    ...(isAdmin ? [{ to: "/admin/categories", icon: FolderOpen, label: "Categories", id: "categories" }] : []),
    ...(isAdmin ? [{ to: "/admin/plans", icon: CreditCard, label: "Wallet Plans", id: "plans" }] : []),
    ...(isAdmin ? [{ to: "/admin/taxes", icon: Percent, label: "Taxes", id: "taxes" }] : []),
    { divider: true, id: "d1" },
    ...(!isRider ? [{ to: "/admin/orders", icon: ClipboardList, label: "Orders", id: "orders" }] : []),
    ...(isAdmin ? [{ to: "/admin/order-serials", icon: Hash, label: "Order Serials", id: "serials" }] : []),
    ...(isAdmin ? [{ to: "/admin/kitchens", icon: ChefHat, label: "Kitchens", id: "kitchens" }] : []),
    ...(isAdmin ? [{ to: "/admin/kitchen-menus", icon: Store, label: "Kitchen Menus", id: "kitchen-menus" }] : []),
    ...(isRider ? [{ to: "/admin/deliveries", icon: Truck, label: "My Deliveries", end: true, id: "deliveries" }] : []),
    { divider: true, id: "d2" },
    ...(isAdmin ? [{ to: "/admin/customers", icon: Users, label: "Customers", id: "customers" }] : []),
    ...(isAdmin ? [{ to: "/admin/recharges", icon: Wallet, label: "Recharges", id: "recharges" }] : []),
    ...(isAdmin ? [{ to: "/admin/reports", icon: BarChart3, label: "Reports", id: "reports" }] : []),
    ...(isAdmin ? [{ to: "/admin/refunds", icon: Undo2, label: "Refunds", id: "refunds" }] : []),
    { divider: true, id: "d3" },
    ...(isAdmin || isChef ? [{ to: "/admin/recipes", icon: ChefHat, label: "Recipes", id: "recipes" }] : []),
    ...(isAdmin ? [{ to: "/admin/recipe-categories", icon: FolderOpen, label: "Recipe Categories", id: "recipe-categories" }] : []),
    ...(isAdmin ? [{ to: "/admin/recipe-tags", icon: Tag, label: "Recipe Tags", id: "recipe-tags" }] : []),
    ...(isAdmin ? [{ to: "/admin/recipe-operational-units", icon: Ruler, label: "Recipe Operational Units", id: "recipe-operational-units" }] : []),
    ...(isAdmin ? [{ to: "/admin/recipe-metric-units", icon: Beaker, label: "Recipe Metric Units", id: "recipe-metric-units" }] : []),
    ...(isAdmin ? [{ to: "/admin/reviews", icon: MessageSquare, label: "Reviews", id: "reviews" }] : []),
    ...(isAdmin ? [{ to: "/admin/superfood-blog", icon: Leaf, label: "Superfood Blog", id: "blog" }] : []),
    ...(isAdmin ? [{ to: "/admin/home-banners", icon: ImagePlus, label: "Home Banners", id: "home-banners" }] : []),
    ...(isAdmin ? [{ to: "/admin/roles", icon: Shield, label: "Role Management", id: "roles" }] : []),
    ...(isAdmin ? [{ to: "/admin/segments", icon: Users2, label: "Customer Segments", id: "segments" }] : []),
    ...(isAdmin ? [{ to: "/admin/cobranding", icon: Building2, label: "Cobranding", id: "cobranding" }] : []),
    ...(isAdmin ? [{ to: "/admin/referrals", icon: Gift, label: "Referrals", id: "referrals" }] : []),
    ...(isAdmin ? [{ to: "/admin/settings", icon: Settings, label: "Settings", id: "settings" }] : []),
    ...(isAdmin ? [{ to: "/admin/discount", icon: Tag, label: "Discount", id: "discount" }] : []),
    ...(isAdmin ? [{ to: "/admin/api-keys", icon: Key, label: "API Keys", id: "api-keys" }] : []),
    ...(isAdmin ? [{ to: "/admin/backup", icon: Database, label: "Backup", id: "backup" }] : []),
    ...(isAdmin ? [{ to: "/admin/enquiries", icon: PhoneCall, label: "Enquiries", id: "enquiries" }] : []),
    { divider: true, id: "d4" },
    { to: "/admin/profile", icon: UserCircle, label: "My Profile", id: "profile" },
  ].filter(item => item.divider || item.to);

  // Mobile nav: only allowed items
  const mobileNavItems = allNavItems.filter(item => item.divider || MOBILE_NAV_IDS.has(item.id));

  const cleanItems = (items) => items.reduce((acc, item, i) => {
    if (item.divider) {
      if (i === 0 || i === items.length - 1) return acc;
      if (acc.length > 0 && acc[acc.length - 1].divider) return acc;
    }
    acc.push(item);
    return acc;
  }, []);

  const desktopItems = cleanItems(allNavItems);
  const mobileItems = cleanItems(mobileNavItems);

  const panelLabel = isRider ? "Rider" : isChef ? "Chef" : isManager ? "Manager" : "Admin";

  const renderNavItems = (items, isMobile, showCollapsed) => (
    <>
      {items.map((item) => {
        if (item.divider) {
          if (showCollapsed) return null;
          return <div key={item.id} className="border-t border-gray-800 my-2" />;
        }
        const Icon = item.icon;
        return (
          <NavLink
            key={item.id}
            to={item.to}
            end={item.end}
            data-testid={`admin-nav-${item.id}`}
            title={showCollapsed ? item.label : undefined}
            onClick={() => isMobile && setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                showCollapsed ? "justify-center" : ""
              } ${isActive ? "bg-green-500/15 text-green-400" : "text-gray-400 hover:text-white hover:bg-gray-800"}`
            }
          >
            <Icon size={20} />
            {!showCollapsed && <span>{item.label}</span>}
          </NavLink>
        );
      })}
    </>
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* ===== DESKTOP SIDEBAR ===== */}
      <aside
        className={`hidden md:flex flex-col bg-gray-900 text-white sticky top-0 self-start h-screen z-40 transition-all duration-200 ${collapsed ? "w-[68px]" : "w-[240px]"}`}
        data-testid="admin-sidebar"
      >
        <div className={`flex items-center justify-between p-4 ${collapsed ? "px-3" : ""}`}>
          {!collapsed ? (
            <div className="flex items-center gap-2">
              <img src={logoSrc(logoUrl)} alt="SM" className="w-9 h-9 object-contain" />
              <div>
                <h1 className="text-base font-bold text-white">Super Morsel</h1>
                <p className="text-gray-400 text-[10px]">{panelLabel} Panel</p>
              </div>
            </div>
          ) : (
            <img src={logoSrc(logoUrl)} alt="SM" className="w-9 h-9 mx-auto object-contain" />
          )}
          <button onClick={toggleDesktopSidebar} className={`p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 ${collapsed ? "mx-auto" : ""}`}>
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
        <nav className="flex-1 space-y-0.5 px-2 overflow-y-auto pb-2">
          {renderNavItems(desktopItems, false, collapsed)}
        </nav>
        <div className={`p-3 ${collapsed ? "px-2" : ""}`}>
          {!collapsed && (
            <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-xl mb-2">
              <div className="w-8 h-8 bg-green-700 rounded-full flex items-center justify-center flex-shrink-0">
                <User size={14} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <p className="text-gray-400 text-[10px] truncate">{user?.email}</p>
              </div>
            </div>
          )}
          <button className={`flex items-center gap-2 w-full p-2.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-xl transition-colors ${collapsed ? "justify-center" : ""}`} onClick={handleLogout} data-testid="admin-logout">
            <LogOut size={18} />
            {!collapsed && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* ===== MOBILE OVERLAY SIDEBAR ===== */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute top-0 left-0 w-[280px] h-full bg-gray-900 text-white flex flex-col shadow-2xl animate-slide-in-left" style={{ paddingTop: "env(safe-area-inset-top, 0)" }} data-testid="mobile-sidebar">
            {/* Header */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-2">
                <img src={logoSrc(logoUrl)} alt="SM" className="w-8 h-8 object-contain" />
                <div>
                  <h1 className="text-sm font-bold text-white">Super Morsel</h1>
                  <p className="text-gray-400 text-[10px]">{panelLabel} Panel</p>
                </div>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400">
                <X size={20} />
              </button>
            </div>
            {/* Mobile Nav */}
            <nav className="flex-1 space-y-0.5 px-2 overflow-y-auto pb-2">
              {renderNavItems(mobileItems, true, false)}
            </nav>
            {/* Desktop notice */}
            <div className="mx-3 mb-2 p-2.5 bg-gray-800/60 rounded-lg border border-gray-700/50">
              <div className="flex items-center gap-2 text-gray-400">
                <Monitor size={14} className="flex-shrink-0" />
                <p className="text-[10px] leading-tight">For full admin features, use the desktop version</p>
              </div>
            </div>
            {/* User + Logout */}
            <div className="p-3">
              <div className="flex items-center gap-3 p-2.5 bg-gray-800 rounded-xl mb-2">
                <div className="w-8 h-8 bg-green-700 rounded-full flex items-center justify-center flex-shrink-0">
                  <User size={14} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">{user?.name}</p>
                  <p className="text-gray-400 text-[9px] truncate">{user?.email}</p>
                </div>
              </div>
              <button className="flex items-center gap-2 w-full p-2.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-xl transition-colors" onClick={handleLogout} data-testid="admin-logout-mobile">
                <LogOut size={18} />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ===== MOBILE TOP BAR ===== */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-gray-900 text-white z-40">
        <div className="flex items-center justify-between px-3 py-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setMobileOpen(true)} className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-300" data-testid="mobile-menu-toggle">
              <Menu size={22} />
            </button>
            <div className="flex items-center gap-2">
              <img src={logoSrc(logoUrl)} alt="SM" className="w-6 h-6 object-contain" />
              <span className="text-sm font-bold">Super Morsel</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdminRole && (
              <button
                onClick={() => setBellOpen((v) => !v)}
                className="relative p-1.5 rounded-lg hover:bg-gray-800 text-gray-300"
                title="Notifications"
                data-testid="admin-notif-bell-mobile"
              >
                <Bell size={16} />
                {unreadAdminCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center" data-testid="admin-notif-count-mobile">
                    {unreadAdminCount > 9 ? "9+" : unreadAdminCount}
                  </span>
                )}
              </button>
            )}
            <span className="text-[9px] text-gray-400 font-medium bg-gray-800 px-2 py-1 rounded-full">{panelLabel}</span>
            <button onClick={handleLogout} className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400" data-testid="mobile-logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* ===== MAIN CONTENT ===== */}
      <main className="flex-1 min-w-0 px-2 py-4 sm:p-4 md:p-6 mt-14 md:mt-0 pb-6 transition-all duration-200">
        {children}
      </main>

      {/* ===== NOTIFICATION BELL (fixed, visible on admin/manager/chef, both desktop & mobile) ===== */}
      {isAdminRole && (
        <>
          <button
            onClick={() => setBellOpen((v) => !v)}
            className="fixed right-4 bottom-4 z-[55] w-10 h-10 rounded-full bg-white border border-gray-200 shadow-lg hidden md:flex items-center justify-center hover:bg-gray-50"
            title="Notifications"
            data-testid="admin-notif-bell"
          >
            <Bell size={18} className="text-gray-700" />
            {unreadAdminCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center ring-2 ring-white" data-testid="admin-notif-count">
                {unreadAdminCount > 9 ? "9+" : unreadAdminCount}
              </span>
            )}
          </button>

          {bellOpen && (
            <>
              <div className="fixed inset-0 z-[54]" onClick={() => setBellOpen(false)} />
              {/* Mobile: dock top-right below mobile top bar. Desktop: float above the bell at bottom-right. */}
              <div
                className="fixed z-[55] w-[92vw] max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-fade-in top-[calc(3.5rem+env(safe-area-inset-top,0px))] right-4 md:top-auto md:bottom-16 md:right-4"
                data-testid="admin-notif-panel"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <h3 className="font-bold text-gray-900 text-sm">Notifications</h3>
                  {adminNotifs.length > 0 && (
                    <button onClick={clearAllNotifs} className="text-[11px] text-gray-500 hover:text-red-600 font-semibold" data-testid="admin-notif-clear">
                      Clear all
                    </button>
                  )}
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {adminNotifs.length === 0 ? (
                    <div className="p-8 text-center text-xs text-gray-400">
                      No notifications yet. New orders will appear here.
                    </div>
                  ) : (
                    adminNotifs.map((n) => (
                      <button
                        key={`${n.order_id}-${n.ts}`}
                        onClick={() => handleNotifClick(n)}
                        className={`w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 flex items-start gap-3 ${
                          !n.read ? "bg-green-50/40" : ""
                        }`}
                        data-testid={`admin-notif-item-${n.order_id}`}
                      >
                        <div className="mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                          {n.read ? (
                            <Check size={14} className="text-green-600" strokeWidth={3} />
                          ) : (
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900">
                              New order {n.order_number || n.order_id.slice(0, 8)}
                            </p>
                            {n.kitchen_code && (
                              <span
                                className={`inline-block text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${kitchenChipClass(n.kitchen_code)}`}
                                data-testid={`admin-notif-kitchen-${n.order_id}`}
                              >
                                {n.kitchen_code}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">
                            {n.user_name} · {n.items_count} item{n.items_count !== 1 ? "s" : ""}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{formatNotifTime(n.ts)}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
